import Cookies from 'js-cookie';

const BASE_URL = 'https://system.sales-management.cloud';
const PUSH_API = `${BASE_URL}/api/push_subscriptions.php`;
const SW_PATH = '/service-worker.js';

/**
 * base64url 文字列を Uint8Array に変換（applicationServerKey 用）
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

/**
 * サーバーから VAPID 公開鍵を取得する
 */
async function fetchVapidPublicKey() {
  const res = await fetch(`${PUSH_API}?action=vapid_public_key`);
  if (!res.ok) throw new Error('VAPID公開鍵の取得に失敗しました');
  const data = await res.json();
  return data.public_key;
}

/**
 * プッシュサブスクリプションをサーバーに保存する
 */
async function saveSubscriptionToServer(subscription) {
  const token = Cookies.get('auth_token');
  if (!token) throw new Error('認証トークンがありません');

  const userAgent = navigator.userAgent || '';
  const deviceType = detectDeviceType(userAgent);

  const res = await fetch(PUSH_API, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      ...subscription.toJSON(),
      device_info: {
        user_agent: userAgent,
        device_type: deviceType,
        platform: navigator.platform || '',
        language: navigator.language || '',
        app_agent: 'sales-management-web',
      },
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'サブスクリプションの保存に失敗しました');
  }
}

function detectDeviceType(userAgent) {
  if (!userAgent) {
    return 'unknown';
  }

  if (/ipad|tablet/i.test(userAgent)) {
    return 'tablet';
  }

  if (/mobi|iphone|ipod|android.+mobile/i.test(userAgent)) {
    return 'mobile';
  }

  if (/android/i.test(userAgent)) {
    return 'tablet';
  }

  return 'desktop';
}

/**
 * プッシュ通知を登録する。
 * - Service Worker を登録
 * - 通知許可を要求
 * - Web Push サブスクリプションを作成してサーバーに保存
 * @returns {Promise<boolean>} 登録成功なら true
 */
export async function registerPushNotification() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.info('[Push] このブラウザはWeb Pushをサポートしていません');
    return false;
  }

  try {
    // Service Worker を登録
    const registration = await navigator.serviceWorker.register(SW_PATH, {
      scope: '/',
    });
    await navigator.serviceWorker.ready;

    // 通知許可を要求
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.info('[Push] 通知の許可が拒否されました');
      return false;
    }

    // VAPID 公開鍵を取得
    const vapidPublicKey = await fetchVapidPublicKey();

    // 既存のサブスクリプションを確認
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });
    }

    // サーバーに保存
    await saveSubscriptionToServer(subscription);
    console.info('[Push] プッシュ通知の登録が完了しました');
    return true;
  } catch (err) {
    console.error('[Push] プッシュ通知の登録エラー:', err);
    return false;
  }
}

/**
 * プッシュ通知の登録を解除する
 */
export async function unregisterPushNotification() {
  if (!('serviceWorker' in navigator)) return;

  try {
    const registration = await navigator.serviceWorker.getRegistration(SW_PATH);
    if (!registration) return;

    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;

    // サーバー側のサブスクリプションを削除
    const token = Cookies.get('auth_token');
    if (token) {
      await fetch(
        `${PUSH_API}?endpoint=${encodeURIComponent(subscription.endpoint)}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    }

    await subscription.unsubscribe();
    console.info('[Push] プッシュ通知の登録を解除しました');
  } catch (err) {
    console.error('[Push] プッシュ通知の解除エラー:', err);
  }
}
